import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  LevelFormat,
  Packer,
  PageOrientation,
  Footer,
  PageNumber,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  UnderlineType,
  WidthType,
  convertInchesToTwip,
} from 'docx';
import {
  resolveTextEditorPrintMargins,
  TEXT_EDITOR_PRINT_MARGIN_IN,
} from './textEditorPrintLayout';
import {
  TEXT_EDITOR_DEFAULT_PAGE_ORIENTATION,
  TEXT_EDITOR_PAGE_ORIENTATION,
  getTextEditorPageLayoutMetrics,
} from './textEditorPageOrientation';

const DEFAULT_FONT = 'Calibri';
const DEFAULT_SIZE = 22;
const BULLET_REF = 'wfm-export-bullets';
const ORDERED_REF = 'wfm-export-ordered';

const HEADING_MAP = {
  H1: HeadingLevel.HEADING_1,
  H2: HeadingLevel.HEADING_2,
  H3: HeadingLevel.HEADING_3,
  H4: HeadingLevel.HEADING_4,
  H5: HeadingLevel.HEADING_5,
  H6: HeadingLevel.HEADING_6,
};

function cssColorToDocxHex(value) {
  if (!value) return undefined;
  const raw = String(value).trim();
  if (!raw || raw === 'transparent' || raw === 'inherit') return undefined;
  const hexMatch = raw.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    return hex.slice(0, 6).toUpperCase();
  }
  const rgbMatch = raw.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return [rgbMatch[1], rgbMatch[2], rgbMatch[3]]
      .map((n) => Number(n).toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }
  return undefined;
}

function parseCssLengthToHalfPoints(value) {
  if (!value) return undefined;
  const raw = String(value).trim().toLowerCase();
  const ptMatch = raw.match(/^([\d.]+)pt$/);
  if (ptMatch) return Math.round(Number(ptMatch[1]) * 2);
  const pxMatch = raw.match(/^([\d.]+)px$/);
  if (pxMatch) return Math.round(Number(pxMatch[1]) * 1.5);
  return undefined;
}

function readStyleProp(el, prop) {
  if (!(el instanceof HTMLElement)) return '';
  return el.style.getPropertyValue(prop.replace(/([A-Z])/g, '-$1').toLowerCase()) || '';
}

function mergeRunStyle(el, inherited = {}) {
  const next = { ...inherited };
  if (!(el instanceof HTMLElement)) return next;

  const color = readStyleProp(el, 'color') || el.getAttribute('color');
  const bg = readStyleProp(el, 'backgroundColor');
  const size = readStyleProp(el, 'fontSize');
  const font = readStyleProp(el, 'fontFamily');
  const weight = readStyleProp(el, 'fontWeight');
  const fontStyle = readStyleProp(el, 'fontStyle');
  const decoration = readStyleProp(el, 'textDecoration');

  const docxColor = cssColorToDocxHex(color);
  if (docxColor) next.color = docxColor;
  const docxBg = cssColorToDocxHex(bg);
  if (docxBg) next.shading = docxBg;
  const docxSize = parseCssLengthToHalfPoints(size);
  if (docxSize) next.size = docxSize;
  if (font) next.font = font.split(',')[0].replace(/['"]/g, '').trim() || DEFAULT_FONT;
  if (Number(weight) >= 600 || weight === 'bold') next.bold = true;
  if (fontStyle === 'italic') next.italics = true;
  if (decoration.includes('underline')) next.underline = UnderlineType.SINGLE;
  if (decoration.includes('line-through')) next.strike = true;

  const tag = el.tagName;
  if (tag === 'STRONG' || tag === 'B') next.bold = true;
  if (tag === 'EM' || tag === 'I') next.italics = true;
  if (tag === 'U') next.underline = UnderlineType.SINGLE;
  if (tag === 'S' || tag === 'STRIKE' || tag === 'DEL') next.strike = true;
  if (tag === 'SUB') next.subScript = true;
  if (tag === 'SUP') next.superScript = true;

  return next;
}

function textRun(text, style) {
  const runOpts = {
    text: text || '',
    font: style.font || DEFAULT_FONT,
    size: style.size || DEFAULT_SIZE,
  };
  if (style.bold) runOpts.bold = true;
  if (style.italics) runOpts.italics = true;
  if (style.underline) runOpts.underline = style.underline;
  if (style.strike) runOpts.strike = true;
  if (style.subScript) runOpts.subScript = true;
  if (style.superScript) runOpts.superScript = true;
  if (style.color) runOpts.color = style.color;
  if (style.shading) {
    runOpts.shading = {
      type: ShadingType.CLEAR,
      fill: style.shading,
      color: style.shading,
    };
  }
  return new TextRun(runOpts);
}

function collectInlineChildren(node, style, out) {
  if (!node) return;
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent;
    if (text) out.push(textRun(text, style));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node;
  const tag = el.tagName;
  if (tag === 'UL' || tag === 'OL' || tag === 'TABLE') return;

  if (tag === 'BR') {
    out.push(new TextRun({ break: 1 }));
    return;
  }

  if (tag === 'A') {
    const href = el.getAttribute('href') || '';
    const linkRuns = [];
    el.childNodes.forEach((child) => collectInlineChildren(child, mergeRunStyle(el, style), linkRuns));
    if (href && linkRuns.length) {
      out.push(
        new ExternalHyperlink({
          children: linkRuns,
          link: href,
        })
      );
    } else {
      el.childNodes.forEach((child) => collectInlineChildren(child, mergeRunStyle(el, style), out));
    }
    return;
  }

  const nextStyle = mergeRunStyle(el, style);
  el.childNodes.forEach((child) => collectInlineChildren(child, nextStyle, out));
}

function paragraphFromElement(el, options = {}) {
  const runs = [];
  el.childNodes.forEach((child) => {
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      (child.tagName === 'UL' || child.tagName === 'OL' || child.tagName === 'TABLE')
    ) {
      return;
    }
    collectInlineChildren(child, mergeRunStyle(el, {}), runs);
  });
  if (!runs.length) runs.push(textRun('', {}));

  const alignRaw = readStyleProp(el, 'textAlign');
  let alignment;
  if (alignRaw === 'center') alignment = AlignmentType.CENTER;
  else if (alignRaw === 'right') alignment = AlignmentType.RIGHT;
  else if (alignRaw === 'justify') alignment = AlignmentType.JUSTIFIED;

  return new Paragraph({
    children: runs,
    heading: options.heading,
    numbering: options.numbering,
    alignment,
  });
}

function tableBorderSide() {
  return {
    style: BorderStyle.SINGLE,
    size: 4,
    color: '94A3B8',
  };
}

function tableFromElement(tableEl) {
  const rows = [];
  tableEl.querySelectorAll('tr').forEach((tr) => {
    const cells = [];
    tr.querySelectorAll('th, td').forEach((cellEl) => {
      const cellBlocks = [];
      cellEl.childNodes.forEach((child) => {
        cellBlocks.push(...blockNodesToDocx(child));
      });
      if (!cellBlocks.length) {
        cellBlocks.push(new Paragraph({ children: [textRun('', {})] }));
      }
      const bg = cssColorToDocxHex(readStyleProp(cellEl, 'backgroundColor'));
      cells.push(
        new TableCell({
          children: cellBlocks,
          shading: bg
            ? {
                type: ShadingType.CLEAR,
                fill: bg,
                color: bg,
              }
            : undefined,
        })
      );
    });
    if (cells.length) rows.push(new TableRow({ children: cells }));
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
    borders: {
      top: tableBorderSide(),
      bottom: tableBorderSide(),
      left: tableBorderSide(),
      right: tableBorderSide(),
      insideHorizontal: tableBorderSide(),
      insideVertical: tableBorderSide(),
    },
  });
}

function blockNodesToDocx(node, listContext = null) {
  if (!node) return [];
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim();
    if (!text) return [];
    return [new Paragraph({ children: [textRun(text, {})] })];
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const el = node;
  const tag = el.tagName;

  if (tag === 'TABLE') {
    return [tableFromElement(el)];
  }

  if (tag === 'UL' || tag === 'OL') {
    const reference = tag === 'UL' ? BULLET_REF : ORDERED_REF;
    const items = [];
    el.querySelectorAll(':scope > li').forEach((li) => {
      const para = paragraphFromElement(li, {
        numbering: { reference, level: 0 },
      });
      items.push(para);
      li.querySelectorAll(':scope > ul, :scope > ol').forEach((nested) => {
        items.push(...blockNodesToDocx(nested));
      });
    });
    return items;
  }

  if (tag === 'LI') {
    return [paragraphFromElement(el, listContext || undefined)];
  }

  if (tag === 'BLOCKQUOTE') {
    return [paragraphFromElement(el)];
  }

  if (HEADING_MAP[tag]) {
    return [
      paragraphFromElement(el, {
        heading: HEADING_MAP[tag],
      }),
    ];
  }

  if (tag === 'P' || tag === 'DIV' || tag === 'PRE') {
    return [paragraphFromElement(el)];
  }

  if (tag === 'HR') {
    return [
      new Paragraph({
        children: [textRun('', {})],
        border: {
          bottom: {
            color: 'CBD5E1',
            space: 1,
            style: BorderStyle.SINGLE,
            size: 6,
          },
        },
      }),
    ];
  }

  return [paragraphFromElement(el)];
}

function htmlToDocxChildren(html) {
  if (!html || typeof document === 'undefined') return [];
  const root = document.createElement('div');
  root.innerHTML = html;
  const blocks = [];
  root.childNodes.forEach((child) => {
    blocks.push(...blockNodesToDocx(child));
  });
  if (!blocks.length) {
    blocks.push(new Paragraph({ children: [textRun('', {})] }));
  }
  return blocks;
}

export async function buildDocxBlobFromExportHtml(
  html,
  title = 'Document',
  pageOrientation = TEXT_EDITOR_DEFAULT_PAGE_ORIENTATION,
  { pageNumbers = true, printMargins } = {}
) {
  const metrics = getTextEditorPageLayoutMetrics(pageOrientation);
  const landscape = metrics.orientation === TEXT_EDITOR_PAGE_ORIENTATION.LANDSCAPE;
  const m = printMargins || resolveTextEditorPrintMargins({ pageNumbers });
  const children = htmlToDocxChildren(html);

  const sectionFooters = pageNumbers
    ? {
        default: new Footer({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: 'Page ',
                  font: DEFAULT_FONT,
                  size: 18,
                  color: '64748B',
                }),
                PageNumber.CURRENT,
              ],
            }),
          ],
        }),
      }
    : undefined;

  const doc = new Document({
    title,
    numbering: {
      config: [
        {
          reference: BULLET_REF,
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: '•',
              alignment: AlignmentType.LEFT,
            },
          ],
        },
        {
          reference: ORDERED_REF,
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.LEFT,
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            size: {
              width: convertInchesToTwip(metrics.widthIn),
              height: convertInchesToTwip(metrics.heightIn),
              orientation: landscape ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT,
            },
            margin: {
              top: convertInchesToTwip(m.topIn),
              right: convertInchesToTwip(m.rightIn),
              bottom: convertInchesToTwip(m.bottomIn),
              left: convertInchesToTwip(m.leftIn),
            },
          },
        },
        footers: sectionFooters,
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
}
